<template>
  <naj-box :title="local.nationalAddress.title">
    <v-row>
      <v-col cols="12">
        <slot />
      </v-col>
    </v-row>
    <template v-if="!hideInputs">
      <v-row>
        <v-col
          v-if="(nationalAddresses || []).length"
          cols="12"
          lg="3"
        >
          <naj-input
            :type-id="formFieldType.selectbox"
            :label="local.nationalAddress.nationalAddresses"
            :value.sync="formModel.id"
            :items="distinctNationalAddressesWithText"
            :clearable="false"
            validates="required"
            item-value="id"
            item-text="text"
            :disabled="disabled"
            @change="addressChanged"
          ></naj-input>
        </v-col>
        <v-col
          v-if="!formModel.city || formModel.cityId"
          cols="12"
          lg="3"
        >
          <naj-input
            :type-id="formFieldType.selectbox"
            :label="local.nationalAddress.city"
            :value.sync="formModel.cityId"
            :value-name.sync="formModel.city"
            :items="cities"
            item-text="name"
            item-value="id"
            :disabled="!cities.length || disabled"
            validates="required"
            @change="cityChanged"
          ></naj-input>
        </v-col>
        <v-col
          v-else
          cols="12"
          lg="3"
        >
          <naj-input
            :type-id="formFieldType.text"
            :label="local.nationalAddress.city"
            :value.sync="formModel.city"
            :validates="isRetrieved ? '' : `required`"
            :disabled="disabled"
            @input="resetRetrievalState"
          ></naj-input>
        </v-col>
        <template v-if="showAddressDetails">
          <v-col
            v-if="!formModel.district"
            cols="12"
            lg="3"
          >
            <naj-input
              :type-id="formFieldType.selectbox"
              :label="local.nationalAddress.district"
              :value.sync="formModel.districtId"
              :value-name.sync="formModel.district"
              :items="districts[formModel.cityId]"
              item-text="name"
              item-value="id"
              :disabled="!(districts[formModel.cityId] || []).length || disabled"
              @change="resetRetrievalState"
            ></naj-input>
          </v-col>
          <v-col
            v-else
            cols="12"
            lg="3"
          >
            <naj-input
              :type-id="formFieldType.text"
              :label="local.nationalAddress.district"
              :value.sync="formModel.district"
              :disabled="disabled"
              @input="resetRetrievalState"
            ></naj-input>
          </v-col>
          <v-col
            cols="12"
            lg="3"
          >
            <naj-input
              :type-id="formFieldType.text"
              :label="local.nationalAddress.street"
              :value.sync="formModel.street"
              :validates="isRetrieved ? '' : 'arabic|max:50'"
              :disabled="disabled"
              @input="resetRetrievalState"
            ></naj-input>
          </v-col>
        </template>
      </v-row>
      <v-row v-if="showAddressDetails">
        <v-col
          cols="12"
          lg="3"
        >
          <naj-input
            :type-id="formFieldType.number"
            :label="local.nationalAddress.postalCode"
            :value.sync="formModel.postalCode"
            :validates="isRetrieved ? '' : 'digits:5'"
            :disabled="disabled"
            @input="resetRetrievalState"
          ></naj-input>
        </v-col>
        <v-col
          cols="12"
          lg="3"
        >
          <naj-input
            :type-id="formFieldType.number"
            :label="local.nationalAddress.poBox"
            :value.sync="formModel.poBox"
            :validates="isRetrieved ? '' : 'digits:4'"
            :disabled="disabled"
            @input="resetRetrievalState"
          ></naj-input>
        </v-col>
        <v-col
          cols="12"
          lg="3"
        >
          <naj-input
            :type-id="formFieldType.number"
            :label="local.nationalAddress.buildingNumber"
            :value.sync="formModel.buildingNumber"
            :validates="isRetrieved ? '' : 'digits:4'"
            :disabled="disabled"
            @input="resetRetrievalState"
          ></naj-input>
        </v-col>
        <v-col
          cols="12"
          lg="3"
        >
          <naj-input
            :type-id="formFieldType.number"
            :label="local.nationalAddress.flatNumber"
            :value.sync="formModel.flatNumber"
            :disabled="disabled"
            @input="resetRetrievalState"
          ></naj-input>
        </v-col>
      </v-row>
    </template>
  </naj-box>
</template>

<script>
import { countriesEnum } from '@/helpers/enum';
import { createNamespacedHelpers } from 'vuex';

const { mapState, mapActions } = createNamespacedHelpers('LookupsModule');
export default {
  inject: ['$validator'],
  props: ['formModel', 'nationalAddresses', 'required', 'hideInputs', 'showMoreAddressDetails'],
  data() {
    return { countriesEnum };
  },
  computed: {
    ...mapState(['countries', 'cities', 'districts']),
    isRetrieved() {
      return Boolean(this.formModel.isRetrieved);
    },
    disabled() {
      return (this.isRetrieved && this.formModel.cityId) || this.isViewMode;
    },
    distinctNationalAddressesWithText() {
      const addresses = (this.nationalAddresses || []);
      return [...new Map(addresses.map(address => [address.text, address])).values()];
    },
    showAddressDetails() {
      return this.showMoreAddressDetails || (!this.isAddressRetrieved && !this.disabled);
    },
    isAddressRetrieved() {
      return (this.nationalAddresses || []).length > 0;
    },
  },
  created() {
    this.getCountries();
    this.getCities();
    if (this.cities && !this.formModel.cityId) this.setNationalAddressCityId();
  },
  methods: {
    ...mapActions(['getCountries', 'getCities', 'getDistrictsByCity']),
    cityChanged() {
      this.getDistrictsByCity(this.formModel.cityId);
      this.resetRetrievalState();
    },
    addressChanged(index) {
      var selectedAddress = this.nationalAddresses[index] || {};
      this.setNationalAddressCityId()
        .then(() => { this.$set(this.formModel, 'isRetrieved', !!this.formModel.cityId || !!this.formModel.city); });
      this.$set(this.formModel, 'district', selectedAddress.district);
      this.$set(this.formModel, 'districtId', selectedAddress.districtId);
      this.$set(this.formModel, 'street', selectedAddress.street);
      this.$set(this.formModel, 'postalCode', selectedAddress.postCode);
      this.$set(this.formModel, 'poBox', selectedAddress.additionalNumber);
      this.$set(this.formModel, 'buildingNumber', selectedAddress.buildingNumber);
      this.$set(this.formModel, 'regionName', selectedAddress.regionName);
      this.$set(this.formModel, 'cityNameEnglish', selectedAddress.cityNameEnglish);
      this.$set(this.formModel, 'flatNumber', null);
    },
    resetRetrievalState() {
      this.$set(this.formModel, 'isRetrieved', false);
    },
    setNationalAddressCityId() {
      return new Promise((resolve, reject) => {
        const city = this.formModel.cityNameEnglish && (this.cities || []).
          find(c => (c.nationalAddressCityEnglishName || '').split(',')
            .some(n => n === this.formModel.cityNameEnglish)) || {};
        this.$set(this.formModel, 'cityId', (city || {}).id ? city.id : null);
        this.$set(this.formModel, 'city', (city || {}).id ? city.name : null);
        if (this.formModel.cityId) this.getDistrictsByCity(this.formModel.cityId);
        if (city) resolve(city.id); else reject(null);
      });
    },
  },
  watch: {
    'formModel.cityNameEnglish'() {
      this.setNationalAddressCityId();
    },
    hideInputs(value) {
      if (value) {
        this.$set(this.formModel, 'cityId', null);
        this.$set(this.formModel, 'city', null);
        this.$set(this.formModel, 'districtId', null);
        this.$set(this.formModel, 'district', null);
        this.$set(this.formModel, 'postalCode', null);
        this.$set(this.formModel, 'poBox', null);
        this.$set(this.formModel, 'buildingNumber', null);
        this.$set(this.formModel, 'street', null);
        this.$set(this.formModel, 'flatNumber', null);
      }
    },
  },
};
</script>
